// dashboardService.js - Firebase-based API functions for dashboard operations
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  getFirebaseDB,
  getFirebaseAuth,
  getFirebaseStorage,
} from "../config/firebase";


// Helper function to get the authentication token
const getToken = async () => {
  const auth = await getFirebaseAuth();
  if (!auth.currentUser) {

    return null;
  }
  return await auth.currentUser.getIdToken();
};

// Get current user ID helper
const getCurrentUserId = async () => {
  const auth = await getFirebaseAuth();
  return auth.currentUser?.uid;
};

// Initialize Firebase database helper
const getDB = async () => {
  return await getFirebaseDB();
};

// Storage usage functions
export async function fetchUserStorageUsage() {
  try {
    const userId = await getCurrentUserId();
    if (!userId) throw new Error("User not authenticated");

    // Get storage usage from profile document
    const database = await getDB();
    const profileDocRef = doc(
      database,
      "users",
      userId,
      "subscription",
      "profile",
    );
    const profileDoc = await getDoc(profileDocRef);

    if (!profileDoc.exists()) {
      return 0;
    }

    const data = profileDoc.data();
    return data.assetStorageCurrent || 0;
  } catch (error) {

    throw error;
  }
}

// Save canvas to Firebase with hierarchical storage
export async function saveCanvasToFirebase(canvasData, canvasId, userId) {
  try {


    if (!userId) {
      throw new Error("User ID is required");
    }

    const database = await getDB();
    const canvasRef = doc(database, "users", userId, "canvases", canvasId);

    // Also update usage count in user profile
    try {
      const profileRef = doc(
        database,
        "users",
        userId,
        "subscription",
        "profile",
      );
      const profileDoc = await getDoc(profileRef);

      if (profileDoc.exists()) {
        const currentCount = profileDoc.data().canvasSavedCount || 0;
        await updateDoc(profileRef, {
          canvasSavedCount: currentCount + 1,
        });
      }
    } catch (profileError) {

    }

    await setDoc(canvasRef, canvasData);

    return { success: true };
  } catch (error) {

    throw error;
  }
}

// Update canvas in Firebase
export async function updateCanvasInFirebase(canvasData, canvasId, userId) {
  try {
    const database = await getDB();
    const canvasRef = doc(database, "users", userId, "canvases", canvasId);
    await updateDoc(canvasRef, canvasData);
    return { success: true };
  } catch (error) {

    throw error;
  }
}

// Add asset metadata to Firebase (legacy function - now handled by upsertAsset)
async function addAssetMetadata(userId, assetData, assetType) {
  try {
    const assetTypeFolder = assetType === "image" ? "images" : "3D_models";
    const metadataFolder =
      assetType === "image" ? "image_metadata" : "3D_model_metadata";

    const database = await getDB();

    // The document ID is extracted from assetData
    const docId = assetData.id;

    // Create a new object for Firestore that does not contain the 'id' field
    const { id, ...dataToSave } = assetData;

    // Use the extracted docId to set the document's unique ID
    const assetRef = doc(
      database,
      "users",
      userId,
      "assets",
      assetTypeFolder,
      metadataFolder,
      docId,
    );

    // Save the cleaned data
    await setDoc(assetRef, dataToSave);

    return { success: true };
  } catch (error) {

    throw error;
  }
}

// Clone canvas functionality
export async function cloneCanvas(sourceCanvasId, userId) {
  try {


    const database = await getDB();

    // Check canvas limits before cloning
    const profileRef = doc(
      database,
      "users",
      userId,
      "subscription",
      "profile",
    );
    const profileDoc = await getDoc(profileRef);

    if (profileDoc.exists()) {
      const profileData = profileDoc.data();
      const currentCount = profileData.canvasSavedCurrent || 0;
      const maxCanvases = profileData.canvasSavedLimit || 20;

      if (currentCount >= maxCanvases) {
        throw new Error(
          `Canvas limit reached. Your plan allows up to ${maxCanvases} saved canvases.`,
        );
      }
    } else {
      // Default limits if profile doesn't exist
      const canvasesRef = collection(database, "users", userId, "canvases");
      const canvasesSnapshot = await getDocs(canvasesRef);

      if (canvasesSnapshot.size >= 20) {
        throw new Error(`Canvas limit reached. Maximum 20 canvases allowed.`);
      }
    }

    // Get the source canvas
    const sourceCanvasRef = doc(
      database,
      "users",
      userId,
      "canvases",
      sourceCanvasId,
    );
    const sourceCanvasDoc = await getDoc(sourceCanvasRef);

    if (!sourceCanvasDoc.exists()) {
      throw new Error("Source canvas not found");
    }

    const sourceData = sourceCanvasDoc.data();
    const newCanvasId = uuidv4();

    // Create new canvas data with "(Copy)" naming convention
    const originalName = sourceData.canvasName || sourceData.name;
    const newCanvasName = `${originalName} (Copy)`;

    const newCanvasData = {
      ...sourceData,
      id: newCanvasId,
      canvasId: newCanvasId,
      canvasName: newCanvasName,
      name: newCanvasName, // Keep both for compatibility
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString(),
    };

    // Save the new canvas
    const newCanvasRef = doc(
      database,
      "users",
      userId,
      "canvases",
      newCanvasId,
    );
    await setDoc(newCanvasRef, newCanvasData);

    // Update user profile canvas count after successful cloning
    if (profileDoc.exists()) {
      const profileData = profileDoc.data();
      const currentCount = profileData.canvasSavedCurrent || 0;
      await updateDoc(profileRef, {
        canvasSavedCurrent: currentCount + 1,
      });
    }


    return newCanvasData;
  } catch (error) {

    throw error;
  }
}

// Fetch assets using production API
export async function fetchUserAssets(userId) {
  try {


    // Get auth token for API call
    const auth = await getFirebaseAuth();
    if (!auth.currentUser) {
      throw new Error("User not authenticated - no current user");
    }

    // Force token refresh to ensure we have a valid token
    const token = await auth.currentUser.getIdToken(true);

    // Use appropriate API URL based on environment
    const apiUrl = import.meta.env.PROD 
      ? "https://youcreate-app-498148088233.australia-southeast2.run.app/api/list-assets"
      : `${window.location.protocol}//${window.location.host}/api/list-assets`;



    // Fetch assets directly from Firebase Storage via backend API
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "Cache-Control": "no-cache",
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch assets from storage");
    }

    const data = await response.json();
    return data.assets; // Use Firebase Storage as source of truth
  } catch (error) {

    throw error;
  }
}

export async function removeAsset(assetId) {
  try {
    const userId = getCurrentUserId();
    if (!userId) throw new Error("User not authenticated");

    const database = await getDB();

    // Try to delete from both organized structures since we don't know the type
    try {
      const imageAssetRef = doc(
        database,
        "users",
        userId,
        "assets",
        "images",
        "image_metadata",
        assetId,
      );
      await deleteDoc(imageAssetRef);
    } catch (error) {
      // Asset might not be in images collection
    }

    try {
      const modelAssetRef = doc(
        database,
        "users",
        userId,
        "assets",
        "3D_models",
        "3D_model_metadata",
        assetId,
      );
      await deleteDoc(modelAssetRef);
    } catch (error) {
      // Asset might not be in 3D_models collection
    }

    return { success: true };
  } catch (error) {

    throw error;
  }
}

// Asset cleanup function to remove references from canvases
export async function cleanupAssetReferences(assetId, userId) {
  try {


    // Get all canvases for the user and remove asset references
    const canvases = await fetchUserCanvases(userId);

    for (const canvas of canvases) {
      let needsUpdate = false;
      const updatedCanvas = { ...canvas };

      // Remove from bgFiles if present
      if (canvas.bgFiles) {
        const originalBgFiles = canvas.bgFiles;
        updatedCanvas.bgFiles = canvas.bgFiles.filter(
          (f) => f.sourceAssetId !== assetId && f.id !== assetId,
        );
        if (updatedCanvas.bgFiles.length !== originalBgFiles.length) {
          needsUpdate = true;
        }
      }

      // Remove from modelFiles if present
      if (canvas.modelFiles) {
        const originalModelFiles = canvas.modelFiles;
        updatedCanvas.modelFiles = canvas.modelFiles.filter(
          (f) => f.sourceAssetId !== assetId && f.id !== assetId,
        );
        if (updatedCanvas.modelFiles.length !== originalModelFiles.length) {
          needsUpdate = true;
        }
      }

      // Remove from layers array if present
      if (canvas.layers) {
        const originalLayers = canvas.layers;
        updatedCanvas.layers = canvas.layers.filter(
          (layer) => layer.sourceAssetId !== assetId && layer.id !== assetId
        );
        if (updatedCanvas.layers.length !== originalLayers.length) {
          needsUpdate = true;
        }
      }

      // Update canvas if needed using new hierarchical structure
      if (needsUpdate) {
        const database = await getDB();
        const canvasRef = doc(database, "users", userId, "canvases", canvas.id);
        await updateDoc(canvasRef, {
          bgFiles: updatedCanvas.bgFiles || [],
          modelFiles: updatedCanvas.modelFiles || [],
          layers: updatedCanvas.layers || [],
          lastModified: new Date().toISOString(),
        });
      }
    }

    return { success: true };
  } catch (error) {

    throw error;
  }
}

// Update asset metadata in Firebase
export async function updateAssetMetadata(userId, assetId, updates) {
  try {
    // Determine asset type and path
    const isModel =
      assetId.includes("model") ||
      assetId.includes("3D") ||
      assetId.includes(".glb") ||
      assetId.includes(".fbx");
    const assetTypeFolder = isModel ? "3D_models" : "images";
    const metadataFolder = isModel ? "3D_model_metadata" : "image_metadata";

    // Clean asset ID to remove any prefixes or path components
    const cleanAssetId = assetId
      .replace(/^(image_|model_)/, "")
      .split("/")
      .pop();

    const database = await getDB();
    const assetRef = doc(
      database,
      "users",
      userId,
      "assets",
      assetTypeFolder,
      metadataFolder,
      cleanAssetId,
    );

    const updateData = {
      ...updates,
      lastModified: new Date().toISOString(),
    };

    await updateDoc(assetRef, updateData);

    return { success: true };
  } catch (error) {

    throw error;
  }
}

// Canvas operations
export async function fetchUserCanvases(userId) {
  try {
    if (!userId) throw new Error("User ID is required");

    const database = await getDB();
    const canvasesRef = collection(database, "users", userId, "canvases");
    const canvasesSnapshot = await getDocs(canvasesRef);

    const canvases = [];
    canvasesSnapshot.forEach((doc) => {
      canvases.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return canvases;
  } catch (error) {

    throw error;
  }
}

// User profile functions
export async function fetchUserProfile(userId) {
  try {
    const actualUserId = userId || (await getCurrentUserId());
    if (!actualUserId) throw new Error("User not authenticated");

    const database = await getDB();
    const profileDocRef = doc(
      database,
      "users",
      actualUserId,
      "subscription",
      "profile",
    );
    const profileDoc = await getDoc(profileDocRef);

    if (!profileDoc.exists()) {
      return null;
    }

    return profileDoc.data();
  } catch (error) {

    throw error;
  }
}

export async function updateUserProfile(userId, updates) {
  try {
    const actualUserId = userId || (await getCurrentUserId());
    if (!actualUserId) throw new Error("User not authenticated");

    const database = await getDB();
    const profileDocRef = doc(
      database,
      "users",
      actualUserId,
      "subscription",
      "profile",
    );
    await updateDoc(profileDocRef, {
      ...updates,
      lastModified: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {

    throw error;
  }
}

// Update user name specifically
export async function updateUserName(userId, newName) {
  if (!userId || !newName) {
    throw new Error("User ID and new name are required.");
  }
  try {
    const database = await getDB();
    const profileRef = doc(database, "users", userId, "subscription", "profile");
    await updateDoc(profileRef, {
      firstName: newName,
    });
    return { success: true };
  } catch (error) {

    throw error;
  }
}

export async function createUserProfile(userId, profileData) {
  try {
    const actualUserId = userId || (await getCurrentUserId());
    if (!actualUserId) throw new Error("User not authenticated");

    const database = await getDB();
    const profileDocRef = doc(
      database,
      "users",
      actualUserId,
      "subscription",
      "profile",
    );
    await setDoc(profileDocRef, {
      ...profileData,
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {

    throw error;
  }
}

// Canvas deletion
export async function deleteCanvas(canvasId, userId) {
  try {
    if (!userId) {
      throw new Error("User ID is required for canvas deletion");
    }

    // Get auth token for API call
    const auth = await getFirebaseAuth();
    if (!auth.currentUser) {
      throw new Error("User not authenticated");
    }

    const token = await auth.currentUser.getIdToken(true);

    // Use appropriate API URL based on environment
    const apiUrl = import.meta.env.PROD 
      ? "https://youcreate-app-498148088233.australia-southeast2.run.app/api/delete-canvas"
      : `${window.location.protocol}//${window.location.host}/api/delete-canvas`;

    const response = await fetch(apiUrl, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ canvasId, userId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to delete canvas");
    }

    return { success: true };
  } catch (error) {

    throw error;
  }
}

// Helper function to read a file as a Base64-encoded string
const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

// Main file upload function - Updated to send JSON with base64 data
export async function uploadFile(file, options = {}) {
  try {


    const auth = await getFirebaseAuth();
    if (!auth.currentUser) {
      throw new Error("User not authenticated");
    }
    const token = await auth.currentUser.getIdToken();
    const userId = auth.currentUser.uid;

    // Convert file to base64
    const fileData = await toBase64(file);

    // Prepare the JSON payload for the server
    const payload = {
      fileData,
      fileName: file.name,
      fileType: file.type,
      folder: options.folder || "images", // Use folder from options
      userId: userId,
    };

    // Use appropriate API URL based on environment
    const apiUrl = import.meta.env.PROD 
      ? "https://youcreate-app-498148088233.australia-southeast2.run.app/api/upload-asset"
      : `${window.location.protocol}//${window.location.host}/api/upload-asset`;

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json", // Set content type to JSON
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload), // Stringify the JSON payload
    });

    if (!response.ok) {
      const errorData = await response.json(); // Expect JSON error response
      throw new Error(
        `Upload failed: ${errorData.error || "Unknown server error"}`,
      );
    }

    const result = await response.json();


    // The backend now returns a relative path, so we don't return downloadURL directly
    return result.filename;
  } catch (error) {

    throw error;
  }
}

// Asset management functions
export async function addAssetReference(userId, assetId, assetData, assetType) {
  try {
    const assetTypeFolder = assetType === "image" ? "images" : "3D_models";
    const metadataFolder =
      assetType === "image" ? "image_metadata" : "3D_model_metadata";

    const database = await getDB();
    const tempRef = doc(
      database,
      "users",
      userId,
      "assets",
      assetType.folder,
      assetType.metadata,
      assetId,
    );

    const referenceData = {
      ...assetData,
      id: assetId,
      userId: userId,
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString(),
    };

    await setDoc(tempRef, referenceData);

    return { success: true };
  } catch (error) {

    throw error;
  }
}

// Generate next available canvas name with sequential numbering
export async function generateNextCanvasName(
  userId,
  baseName = "Saved Canvas",
) {
  try {
    const database = await getDB();
    const canvasesRef = collection(database, "users", userId, "canvases");
    const canvasesSnapshot = await getDocs(canvasesRef);

    let maxNumber = 0;
    const nameRegex = new RegExp(
      `^${baseName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")} (\\d+)$`,
    );

    canvasesSnapshot.forEach((doc) => {
      const canvasData = doc.data();
      const name = canvasData.canvasName || canvasData.name;
      if (name && name.match(nameRegex)) {
        const number = parseInt(name.match(nameRegex)[1], 10);
        if (number > maxNumber) {
          maxNumber = number;
        }
      }
    });

    return `${baseName} ${maxNumber + 1}`;
  } catch (error) {

    // Fallback to timestamp if error occurs
    return `${baseName} ${Date.now()}`;
  }
}

// Additional utility functions needed by Dashboard
export const duplicateCanvas = cloneCanvas; // Alias for cloneCanvas

export async function renameCanvas(canvasId, newName, userId) {
  try {
    const database = await getDB();
    const canvasRef = doc(database, "users", userId, "canvases", canvasId);
    await updateDoc(canvasRef, {
      name: newName,
      canvasName: newName, // Update both for compatibility
      lastModified: new Date().toISOString(),
    });
    return { success: true };
  } catch (error) {

    throw error;
  }
}

export async function renameAsset(assetId, newName, userId) {
  try {
    await updateAssetMetadata(userId, assetId, { name: newName });
    return { success: true };
  } catch (error) {

    throw error;
  }
}

// This function now correctly maps arguments to the new uploadFile function
export async function uploadFileToStorage(file, folder, onProgress) {
  // Note: onProgress is not used in the base64 approach, but kept for API consistency.
  const assetType = folder === "models" ? "model" : "image";
  return await uploadFile(file, { folder, assetType });
}

export function getStorageLimitBytes(plan = "essential") {
  const FIVE_GB = 5 * 1000 * 1000 * 1000;
  const FIFTEEN_GB = 15 * 1000 * 1000 * 1000;
  return plan === "pro" ? FIFTEEN_GB : FIVE_GB;
}

export function truncateName(name, maxLength = 15) {
  if (!name || name.length <= maxLength) return name;
  return name.substring(0, maxLength - 3) + "...";
}

export async function computeFileHash(file) {
  // Simple hash based on file name, size, and last modified
  const hashInput = `${file.name}-${file.size}-${file.lastModified || Date.now()}`;
  return btoa(hashInput)
    .replace(/[^a-zA-Z0-9]/g, "")
    .substring(0, 16);
}

export async function deleteAsset(assetId, userId, assetType) {
  try {
    if (!userId || !assetId || !assetType) {
      throw new Error(
        "User ID, Asset ID, and Asset Type are required for deletion",
      );
    }

    // Get auth token for API call
    const auth = await getFirebaseAuth();
    if (!auth.currentUser) {
      throw new Error("User not authenticated");
    }

    const token = await auth.currentUser.getIdToken(true);

    // Pass assetId, userId, AND assetType as query parameters
    const params = new URLSearchParams({ assetId, userId, assetType });

    // Use appropriate API URL based on environment
    const baseUrl = import.meta.env.PROD 
      ? "https://youcreate-app-498148088233.australia-southeast2.run.app"
      : `${window.location.protocol}//${window.location.host}`;
    const apiUrl = `${baseUrl}/api/delete-asset?${params.toString()}`;

    const response = await fetch(apiUrl, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to delete asset");
    }

    // Clean up asset references from canvases
    await cleanupAssetReferences(assetId, userId);

    return { success: true };
  } catch (error) {

    throw error;
  }
}

export async function bulkDeleteCanvases(canvasIds, userId) {
  try {
    if (!userId) {
      throw new Error("User ID is required for bulk canvas deletion");
    }

    // Get auth token for API call
    const auth = await getFirebaseAuth();
    if (!auth.currentUser) {
      throw new Error("User not authenticated");
    }

    const token = await auth.currentUser.getIdToken(true);

    // Use appropriate API URL based on environment
    const apiUrl = import.meta.env.PROD 
      ? "https://youcreate-app-498148088233.australia-southeast2.run.app/api/bulk-delete-canvases"
      : `${window.location.protocol}//${window.location.host}/api/bulk-delete-canvases`;

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ canvasIds, userId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to bulk delete canvases");
    }

    const result = await response.json();
    return result;
  } catch (error) {

    throw error;
  }
}

export async function upsertAsset(userId, assetData) {
  try {
    if (!userId || !assetData || !assetData.id || !assetData.assetType) {
      throw new Error(
        "Invalid arguments for upsertAsset. A userId, assetData object with id, and assetType are required.",
      );
    }



    const database = await getDB();

    // Determine the correct sub-collection based on asset type
    const assetCollectionPath =
      assetData.assetType === "image" ? "image_metadata" : "3D_model_metadata";

    // Construct the final, correct path
    const assetRef = doc(
      database,
      "users",
      userId,
      "assets",
      assetData.assetType === "image" ? "images" : "3D_models",
      assetCollectionPath,
      assetData.id,
    );



    // Create a clean data object without the redundant ID field.
    const { id, ...dataToSave } = assetData;

    await setDoc(assetRef, dataToSave);



    return { success: true };
  } catch (error) {

    throw error;
  }
}

export async function bulkDeleteAssets(assetIds, userId) {
  try {
    if (!userId) {
      throw new Error("User ID is required for bulk asset deletion");
    }

    // Get auth token for API call
    const auth = await getFirebaseAuth();
    if (!auth.currentUser) {
      throw new Error("User not authenticated");
    }

    const token = await auth.currentUser.getIdToken(true);

    // Use appropriate API URL based on environment
    const apiUrl = import.meta.env.PROD 
      ? "https://youcreate-app-498148088233.australia-southeast2.run.app/api/bulk-delete-assets"
      : `${window.location.protocol}//${window.location.host}/api/bulk-delete-assets`;

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ assetIds, userId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to bulk delete assets");
    }

    const result = await response.json();

    // Clean up asset references from canvases for successfully deleted assets
    if (result.successfulDeletions && result.successfulDeletions.length > 0) {
      for (const assetId of result.successfulDeletions) {
        try {
          await cleanupAssetReferences(assetId, userId);
        } catch (cleanupError) {
          // Silently continue if cleanup fails
        }
      }
    }

    return result;
  } catch (error) {

    throw error;
  }
}

export async function getCurrentCanvasCount() {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      // Return 0 if no user is logged in
      return 0;
    }
    const canvases = await fetchUserCanvases(userId);
    return canvases.length;
  } catch (error) {

    return 0; // Return 0 in case of an error
  }
}

export async function getCanvasLimit() {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      return 20; // Default limit for non-logged-in users
    }

    const userProfile = await fetchUserProfile(userId);
    if (userProfile && userProfile.subscriptionPlan === "pro") {
      return 100; // Limit for Pro plan
    }
    return 20; // Default limit for Essential plan
  } catch (error) {

    return 20; // Return default limit in case of an error
  }
}

// --- CANVAS API FUNCTIONS ---

export const getUserCanvases = async () => {
  const token = await getToken();
  if (!token) {
    throw new Error("User not authenticated");
  }

  // Use appropriate API URL based on environment
  const url = import.meta.env.PROD 
    ? "https://youcreate-app-498148088233.australia-southeast2.run.app/api/canvases/canvases"
    : `${window.location.protocol}//${window.location.host}/api/canvases/canvases`;



  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {

    throw new Error("Failed to fetch canvases");
  }
  return response.json();
};

export const saveCanvasState = async (canvasData) => {
  const token = await getToken();
  if (!token) {
    throw new Error("User not authenticated");
  }

  // Use appropriate API URL based on environment
  const url = import.meta.env.PROD 
    ? "https://youcreate-app-498148088233.australia-southeast2.run.app/api/canvases/upsert"
    : `${window.location.protocol}//${window.location.host}/api/canvases/upsert`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(canvasData),
  });

  if (!response.ok) {
    throw new Error(`Failed to save canvas state. Status: ${response.status}`);
  }

  return response.json();
};

export const removeCanvas = async (canvasId) => {
  const token = await getToken();
  if (!token) {
    throw new Error("User not authenticated");
  }

  // Use appropriate API URL based on environment
  const url = import.meta.env.PROD 
    ? `https://youcreate-app-498148088233.australia-southeast2.run.app/api/canvases/${canvasId}`
    : `${window.location.protocol}//${window.location.host}/api/canvases/${canvasId}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to delete canvas. Status: ${response.status}`);
  }

  return response.json();
};

export const unlinkAssetFromAllCanvases = async (assetId, userId) => {
  try {
    const db = await getDB();
    const canvasesRef = collection(db, "users", userId, "canvases");
    const canvasesSnapshot = await getDocs(canvasesRef);

    const updatePromises = [];

    canvasesSnapshot.forEach((canvasDoc) => {
      const canvasData = canvasDoc.data();
      let needsUpdate = false;
      const updatedData = { ...canvasData };

      // Remove from bgFiles if present
      if (canvasData.bgFiles) {
        const filteredBgFiles = canvasData.bgFiles.filter(
          (file) => file.sourceAssetId !== assetId && file.id !== assetId
        );
        if (filteredBgFiles.length !== canvasData.bgFiles.length) {
          updatedData.bgFiles = filteredBgFiles;
          needsUpdate = true;
        }
      }

      // Remove from modelFiles if present
      if (canvasData.modelFiles) {
        const filteredModelFiles = canvasData.modelFiles.filter(
          (file) => file.sourceAssetId !== assetId && file.id !== assetId
        );
        if (filteredModelFiles.length !== canvasData.modelFiles.length) {
          updatedData.modelFiles = filteredModelFiles;
          needsUpdate = true;
        }
      }

      if (needsUpdate) {
        const canvasRef = doc(db, "users", userId, "canvases", canvasDoc.id);
        updatePromises.push(updateDoc(canvasRef, updatedData));
      }
    });

    if (updatePromises.length > 0) {
      await Promise.all(updatePromises);

    }

    return { success: true, canvasesUpdated: updatePromises.length };
  } catch (error) {

    throw error;
  }
};
